import type { SopAgent } from '../ports/sop-agent.js';
import { err, ok } from '../core/result.js';

/**
 * Mock SopAgent — deterministic drafting templates (ADR 0004 mock-always).
 *
 * Produces useful, honest markdown for each known step so the simulation
 * demonstrates the real operator experience; a live LLM adapter swaps in at
 * the composition root later (backlog N1).
 */
const sar = (halalas: number): string => `SAR ${(halalas / 100).toLocaleString('en-US')}`;

export function createMockSopAgent(): SopAgent {
  return {
    async draft({ playbook, step, employee, input }, signal) {
      if (signal.aborted) return err('cancelled', 'Draft was cancelled');

      const subject = employee ? `${employee.nameEn} (${employee.jobTitle})` : 'the subject';
      const lines: string[] = [`## ${step.title}`, ''];

      if (playbook.key === 'onboarding') {
        lines.push(
          `Onboarding pack for **${subject}** — drafted by the People Engine agent (simulation).`,
          '',
          '- [ ] Employment contract signed and uploaded to Qiwa',
          '- [ ] GOSI registration submitted (wage base = basic + housing + transport)',
          '- [ ] Medical insurance activated and card issued',
          '- [ ] Bank account / IBAN collected for payroll (WPS)',
          '- [ ] Workspace, email and system access provisioned',
          '- [ ] Probation objectives agreed with the manager',
        );
        if (employee && !employee.saudiNational) {
          lines.push('- [ ] Iqama transfer/issuance confirmed and expiry tracked');
        }
      } else if (playbook.key === 'hr-letter' && employee) {
        const kind = input?.['kind'] ?? 'salary-certificate';
        const total = employee.salary.basic + employee.salary.housing + employee.salary.transport + employee.salary.other;
        if (kind === 'salary-certificate') {
          lines.push(
            '**To whom it may concern**',
            '',
            `This is to certify that **${employee.nameEn}** (ID/Iqama ${employee.nationalId}) is employed with us as **${employee.jobTitle}** since ${employee.contract.startDate}, with a gross monthly salary of **${sar(total)}** (basic ${sar(employee.salary.basic)}, housing ${sar(employee.salary.housing)}, transport ${sar(employee.salary.transport)}).`,
            '',
            'This certificate is issued upon the employee’s request without any liability on the company.',
            '',
            '---',
            '',
            '**إلى من يهمه الأمر**',
            '',
            `نفيد بأن **${employee.nameAr}** (هوية/إقامة ${employee.nationalId}) يعمل لدينا بوظيفة **${employee.jobTitle}** منذ ${employee.contract.startDate}، وبراتب شهري إجمالي قدره **${sar(total)}**.`,
            '',
            'صدرت هذه الشهادة بناءً على طلب الموظف دون أدنى مسؤولية على الشركة.',
          );
        } else {
          lines.push(
            '**To whom it may concern**',
            '',
            `This is to certify that **${employee.nameEn}** (ID/Iqama ${employee.nationalId}) has been employed with us as **${employee.jobTitle}** since ${employee.contract.startDate} and remains in good standing.`,
            '',
            '---',
            '',
            '**إلى من يهمه الأمر**',
            '',
            `نفيد بأن **${employee.nameAr}** (هوية/إقامة ${employee.nationalId}) يعمل لدينا بوظيفة **${employee.jobTitle}** منذ ${employee.contract.startDate} وما زال على رأس العمل.`,
          );
        }
      } else if (playbook.key === 'doc-renewal') {
        lines.push(
          `Renewal plan for **${subject}** — drafted by the People Engine agent (simulation).`,
          '',
          '- [ ] Confirm the expiring document and its exact expiry date with the employee',
          '- [ ] Initiate the renewal on the owning platform (Muqeem/Absher for iqama, insurer portal for medical, Qiwa for contracts)',
          '- [ ] Budget the government/insurer fees and route for payment approval',
          '- [ ] Update GOSI/Qiwa records if the renewal changes registered details',
          '- [ ] Upload the renewed document and set the next expiry watch',
        );
      } else if (playbook.key === 'leave-request') {
        lines.push(
          `Leave decision memo for **${subject}** — drafted by the People Engine agent (simulation).`,
          '',
          '- The request has been costed in working days against the annual entitlement (see the policy verdict above).',
          '- Weekends (Fri–Sat) and KSA public holidays in the span cost nothing.',
          '- Check team coverage for the requested window before approving.',
          '- On approval the balance is deducted automatically and the employee is notified.',
        );
      } else {
        lines.push(`Draft for **${subject}** — step \`${step.id}\` of playbook \`${playbook.key}\` v${playbook.version}.`);
      }

      return ok(lines.join('\n'));
    },
  };
}
